import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import NoDataText from '../../components/NoDataText';
import TableContainer from '../../components/Table/TableContainer';
import { VENDOR_PAYMENT_HISTORY_LIST_TABLE_LABELS } from '../../constants/tableLabels';
import usePagination from '../../hooks/usePagination';
import { ActiveSortField, PaymentHistoryData } from '../../types';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import PaymentHistoryTableDataRow from './PaymentHistoryTableDataRow';
import TableActions from '../../components/Table/TableActions';
import Loader from '../../components/Loader';
import { getVendorPaymentHistory } from '../../api/vendorApi/vendorAccountsApi';
import { downloadCsvorExcel } from '../../helpers/downloadCsv';
import useSnackBar from '../../hooks/useSnackBar';

interface PaymentHistoryTableProps {
    vendorId: number;
}

const PaymentHistoryTable = ({ vendorId }: PaymentHistoryTableProps) => {
    const params = useParams();
    const { setAlert } = useSnackBar();

    const { pageNumber, rowsPerPage, setTotalCount } = usePagination();

    const [paymentHistoryList, setPaymentHistoryList] = useState<PaymentHistoryData[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [isAccountingIntegrated, setIsAccountingIntegrated] = useState<boolean>(false);

    const [match, setMatch] = useState<string>('');
    const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);


    const hasParams = !!Object.entries(params).length;

    const sortableFields = {
        updatedAt: {
            order: '',
            fieldName: 'updatedAt',
        },
    };

    useEffect(() => {
        getVendorPaymentHistory(vendorId, {
            page: pageNumber,
            size: rowsPerPage,
            count: pageNumber === 1,
            match,
            activeSortField,
        }).then((res) => {
            if (res.data) {
                if (pageNumber === 1) {
                    setTotalCount?.(res.data.totalCount || 0);
                    setIsAccountingIntegrated(res.data.isAccountingIntegrated ?? false);
                }
                setPaymentHistoryList(res.data.resultSet || []);
                setIsDataLoading(false);
            }
        })

    }, [pageNumber, rowsPerPage, match, activeSortField]);

    const exportList = () => {
        downloadCsvorExcel(`/v1/fo/vendors/${vendorId}/payment-history/export`, 'POST', 'Vendor_Payment_History', 'csv', {
            page: pageNumber,
            size: rowsPerPage,
            count: true,
            match: match,
        }).then((res) => {
            if (res?.data) {
                setAlert({
                    message: 'Vendor Payment history exported successfully.',
                    type: 'success',
                });
            }
        });
    };    

    return (

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

            {isDataLoading && !paymentHistoryList.length ? (
                <Box position='absolute' top='50%' left='50%'>
                    <Loader colorType='warning' />
                </Box>
            ) : (
                <>
                    <TableActions
                        hasParams={hasParams}
                        match={match}
                        sx={{ mt: '5px' }}
                        setMatch={setMatch}
                        exportList={exportList}
                    />

                    {!isDataLoading && paymentHistoryList.length === 0 ? (
                        <NoDataText marginTop='20px' />
                    ) : (
                        <TableContainer
                            headerLabels={isAccountingIntegrated ? VENDOR_PAYMENT_HISTORY_LIST_TABLE_LABELS : VENDOR_PAYMENT_HISTORY_LIST_TABLE_LABELS.filter((col) => col.label !== 'Netsuite')}
                            sortableFields={sortableFields}
                            activeSortField={activeSortField}
                            setActiveSortField={setActiveSortField}
                            handleSortTableData={handleSortTableData}
                        >
                            <PaymentHistoryTableDataRow
                                data={paymentHistoryList}
                                isAccountingIntegrated={isAccountingIntegrated}
                            />
                        </TableContainer>
                    )}
                </>
            )}
        </Box>
    );
};

export default PaymentHistoryTable;